import React, {useState} from "react";
import Tooltip from "@mui/material/Tooltip";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CropIcon from "@mui/icons-material/Crop";
import CategoryIcon from "@mui/icons-material/Category";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import {observer} from "mobx-react-lite";
import {ApplicationStore, CropConfig} from "../../../librettoEditor/store/ApplicationStore";
import {CompositionService} from "../../../librettoEditor/services/CompositionService";
import {VideoClip} from "../../../engine/clips/video/video";
import {isScriptClip, ScriptLayerClip} from "../../../engine/clips/script";
import { useTheme } from "@mui/material/styles";

type Props = {
    containerRef: React.RefObject<HTMLDivElement>;
};

const CanvasClipToolbar: React.FC<Props> = observer(({containerRef}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const textPrimaryColor = isDarkMode ? "var(--app-text-primary)" : "#111";
    const subtleBgColor = "var(--app-bg-subtle)";
    const surfaceBgColor = "var(--app-bg-surface)";
    const borderSubtleColor = "var(--app-border-subtle)";
    const borderMutedColor = "var(--app-border-muted)";
    const mutedTextColor = "var(--app-text-muted)";
    const selectedVisualElement = ApplicationStore.selectedVisualElement;
    const isCropMode = ApplicationStore.isCropMode;
    const [posMenuOpen, setPosMenuOpen] = useState(false);
    const [posAnchor, setPosAnchor] = useState<HTMLElement | null>(null);
    const [shapesOpen, setShapesOpen] = useState(false);
    const [shapesAnchor, setShapesAnchor] = useState<HTMLElement | null>(null);
    const [zoomStr, setZoomStr] = useState('100');

    // Hide toolbar when in crop mode (CropContainer shows its own toolbar)
    if (!selectedVisualElement || isCropMode) return null;

    // Don't show this toolbar for captions clips - they have their own CaptionsCanvasToolbar
    const clip = CompositionService.getClipById(selectedVisualElement.clipId);
    if (clip?.type === 'captions' || clip?.type === 'text') return null;

    const isScriptLayer = !!clip && isScriptClip(clip);
    const selectedSourceIndex = isScriptLayer && Number.isFinite(Number(selectedVisualElement.sourceIndex))
        ? Number(selectedVisualElement.sourceIndex)
        : null;

    const canRotate =
        !!clip &&
        (
            (isScriptLayer && selectedSourceIndex !== null && typeof (clip as any).updateVideoSourceTransform === 'function') ||
            (!isScriptLayer && (String(clip.type) === 'video' || String(clip.type) === 'image'))
        );

    const handleRotate90 = (clockwise: boolean) => {
        if (!clip) return;
        const scope = ApplicationStore.sceneEditScope;

        // ScriptLayerClip: rotate the selected video source (applies to the whole source).
        if (isScriptClip(clip)) {
            const sourceIndex = selectedSourceIndex;
            if (sourceIndex === null) return;
            if (typeof (clip as any).updateVideoSourceTransform !== 'function') return;

            const currentVisual =
                typeof (clip as any).getVideoSourceVisual === 'function'
                    ? (clip as any).getVideoSourceVisual(sourceIndex)
                    : undefined;
            const currentDeg = Number(currentVisual?.rotation ?? 0);
            const snappedDeg = Math.round(currentDeg / 90) * 90;
            const nextDeg = snappedDeg + (clockwise ? 90 : -90);
            const normalized = ((nextDeg % 360) + 360) % 360;

            try {
                (clip as any).updateVideoSourceTransform(sourceIndex, { rotation: normalized });
                CompositionService.recomputeFrame();
                try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
            } catch {}

            try {
                CompositionService.notifyClipUpdated(clip);
            } catch {}
            return;
        }

        // Regular video/image clips: rotate the clip.
        if (!(String(clip.type) === 'video' || String(clip.type) === 'image')) return;

        const currentDeg = Number((clip as any).rotation ?? 0);
        const snappedDeg = Math.round(currentDeg / 90) * 90;
        const nextDeg = snappedDeg + (clockwise ? 90 : -90);
        const normalized = ((nextDeg % 360) + 360) % 360;

        try {
            const spansMultipleScenes = CompositionService.clipSpansMultipleScenes(clip);
            const useSceneOverrides = scope === "scene" && spansMultipleScenes;
            const useAllScope = scope === "all" && spansMultipleScenes;

            if (useSceneOverrides) {
                const sceneKey = CompositionService.getCurrentSceneKey();
                if (sceneKey) {
                    CompositionService.setSceneVisualOverridesForClip(clip, sceneKey, { rotation: normalized });
                } else {
                    (clip as any).rotation = normalized;
                }
            } else {
                if (useAllScope) {
                    CompositionService.clearSceneVisualOverrideKeysForClip(clip, ["rotation"]);
                }
                (clip as any).rotation = normalized;
            }
            CompositionService.recomputeFrame();
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        } catch {}

        try {
            CompositionService.notifyClipUpdated(clip);
        } catch {}
    };

    const toolbarStyle: React.CSSProperties = {
        position: 'absolute',
        top: 8,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: surfaceBgColor,
        border: `1px solid ${borderSubtleColor}`,
        borderRadius: 10,
        padding: '6px 8px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
    };

    const btnStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 28,
        padding: '0 10px',
        borderRadius: 8,
        border: `1px solid ${borderSubtleColor}`,
        background: surfaceBgColor,
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: 12,
        color: textPrimaryColor,
    };

    return (
        <div
            style={toolbarStyle}
            onMouseDown={(e) => {
                // Prevent deselection when interacting with toolbar
                e.stopPropagation();
            }}
        >
            {/* Position dropdown */}
            <div
                style={btnStyle}
                onClick={(e) => {
                    setPosAnchor(e.currentTarget as HTMLElement);
                    setPosMenuOpen(o => !o);
                }}
            >
                Position
            </div>
            <Popper open={posMenuOpen} anchorEl={posAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setPosMenuOpen(false)}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 0.5,
                            borderRadius: '10px',
                            backgroundColor: surfaceBgColor,
                            border: `1px solid ${isDarkMode ? borderSubtleColor : "rgba(15, 15, 15, 0.06)"}`,
                            "& .MuiMenuItem-root": {
                                color: textPrimaryColor,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: subtleBgColor },
                            },
                        }}
                    >
                        <MenuList dense>
                            <MenuItem onClick={() => {
                                try {
                                    CompositionService.setClipPositionPreset(selectedVisualElement, 'fill');
                                } catch {}
                                setPosMenuOpen(false);
                            }}>
                                Fill scene editor
                            </MenuItem>
                            <MenuItem onClick={() => {
                                try {
                                    CompositionService.setClipPositionPreset(selectedVisualElement, 'fit');
                                } catch {}
                                setPosMenuOpen(false);
                            }}>
                                Fit scene editor
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>

            {/* Zoom percent */}
            <Tooltip title="Zoom" placement="top">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TextField
                        size="small"
                        value={zoomStr}
                        onChange={(e) => setZoomStr(e.target.value.replace(/[^0-9.]/g, ''))}
                        onBlur={() => {
                            const pct = Math.max(100, Math.min(1000, Number(zoomStr) || 100));
                            try {
                                CompositionService.setClipZoomPercent(selectedVisualElement, pct);
                            } catch {}
                            setZoomStr(String(pct));
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                const pct = Math.max(100, Math.min(1000, Number(zoomStr) || 100));
                                try {
                                    CompositionService.setClipZoomPercent(selectedVisualElement, pct);
                                } catch {}
                                setZoomStr(String(pct));
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: 12, width: 44, textAlign: 'right' } }}
                        InputProps={{ endAdornment: <span style={{ fontSize: 11, color: mutedTextColor, marginLeft: 4 }}>%</span> }}
                    />
                </div>
            </Tooltip>

            {/* Rotate 90° */}
            {canRotate && (
                <Tooltip
                    placement="top"
                    title={"Rotate. Hold shift to switch direction."}
                >
                    <div
                        style={btnStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRotate90(!e.shiftKey);
                        }}
                    >
                        <RotateRightIcon sx={{ fontSize: 16, color: textPrimaryColor }} />
                    </div>
                </Tooltip>
            )}

            {/* Crop */}
            <Tooltip title="Enter Crop Mode" placement="top">
                <div
                    style={{
                        ...btnStyle,
                        ...(ApplicationStore.isCropMode
                            ? {
                                  background: isDarkMode ? subtleBgColor : '#f0f0f0',
                                  borderColor: isDarkMode ? borderMutedColor : '#333',
                              }
                            : {})
                    }}
                    onClick={() => {
                        if (ApplicationStore.isCropMode) {
                            ApplicationStore.exitCropMode();
                        } else {
                            // Get current crop from the clip (if any) to use as initial crop
                            let initialCrop: CropConfig | undefined;
                            if (selectedVisualElement) {
                                const clip = CompositionService.getClipById(selectedVisualElement.clipId);
                                if (clip instanceof VideoClip) {
                                    initialCrop = clip.getCrop();
                                } else if (clip && isScriptClip(clip)) {
                                    const sourceIndex = selectedVisualElement.sourceIndex ?? 0;
                                    const scriptClip = clip as ScriptLayerClip;
                                    if (ApplicationStore.cropScope === 'current') {
                                        const absCompTime = Number(CompositionService.composition?.currentTime?.seconds ?? 0);
                                        const clipLocalTime = scriptClip.mapCompTimeToClipLocal(absCompTime);
                                        const sceneIndex = clipLocalTime != null ? scriptClip.findSceneIndexForTime(clipLocalTime) : 0;
                                        initialCrop =
                                            scriptClip.getSceneVisualProperties(sceneIndex, sourceIndex)?.crop ??
                                            scriptClip.getVideoSourceCrop(sourceIndex);
                                    } else {
                                        initialCrop = scriptClip.getVideoSourceCrop(sourceIndex);
                                    }
                                }
                            }
                            ApplicationStore.enterCropMode(initialCrop);
                            setTimeout(() => {CompositionService.recomputeFrame()}, 80);
                        }
                    }}
                >
                    <CropIcon
                        sx={{
                            fontSize: 16,
                            color: ApplicationStore.isCropMode
                                ? (isDarkMode ? "var(--app-text-primary)" : '#333')
                                : textPrimaryColor,
                        }}
                    />
                </div>
            </Tooltip>

            {/* Shapes */}
            <Tooltip title="Shape" placement="top">
                <div
                    style={btnStyle}
                    onClick={(e) => {
                        setShapesAnchor(e.currentTarget as HTMLElement);
                        setShapesOpen(o => !o);
                    }}
                >
                    <CategoryIcon sx={{ fontSize: 16, color: textPrimaryColor }}/>
                </div>
            </Tooltip>
            <Popper open={shapesOpen} anchorEl={shapesAnchor} placement="bottom-start" sx={{ zIndex: 9999 }}>
                <ClickAwayListener onClickAway={() => setShapesOpen(false)}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 0.5,
                            borderRadius: '10px',
                            backgroundColor: surfaceBgColor,
                            border: `1px solid ${isDarkMode ? borderSubtleColor : "rgba(15, 15, 15, 0.06)"}`,
                            "& .MuiMenuItem-root": {
                                color: textPrimaryColor,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: subtleBgColor },
                            },
                        }}
                    >
                        <MenuList dense>
                            <MenuItem
                                onClick={() => {
                                    try { CompositionService.setClipMaskShape(selectedVisualElement, 'none' as any); } catch {}
                                    setShapesOpen(false);
                                }}
                            >
                                Rectangle
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    try { CompositionService.setClipMaskShape(selectedVisualElement, 'circle' as any); } catch {}
                                    setShapesOpen(false);
                                }}
                            >
                                Circle
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    );
});

export default CanvasClipToolbar;
